import {
  type CommandHistorySettings,
  type ScriptEditor,
} from '@deephaven/console';
import {
  assertIsDashboardPluginProps,
  type DashboardPluginComponentProps,
  DashboardUtils,
  type DehydratedPanelProps,
  type DehydratedDashboardPanelProps,
  LayoutUtils,
  type PanelComponent,
  type PanelHydrateFunction,
  useListener,
  usePanelRegistration,
} from '@deephaven/dashboard';
import { FileUtils } from '@deephaven/file-explorer';
import { type CloseOptions, isComponent } from '@deephaven/golden-layout';
import { type dh } from '@deephaven/jsapi-types';
import Log from '@deephaven/log';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { nanoid } from 'nanoid';
import { ConsoleEvent, NotebookEvent } from './events';
import {
  ConsolePanel,
  CommandHistoryPanel,
  FileExplorerPanel,
  LogPanel,
  NotebookPanel,
} from './panels';
import { setDashboardConsoleSettings } from './redux';
import useConfigureRuff from './useConfigureRuff';

const log = Log.module('ConsolePlugin');

type NotebookPanelComponent = PanelComponent & {
  notebook: ScriptEditor;
  focus: () => void;
};

function isNotebookPanel(
  panel: PanelComponent
): panel is NotebookPanelComponent {
  return (panel as NotebookPanelComponent).notebook !== undefined;
}

export type ConsolePluginProps = DashboardPluginComponentProps & {
  hydrateConsole?: PanelHydrateFunction;
  notebooksUrl: string;
};

function assertIsConsolePluginProps(
  props: Partial<ConsolePluginProps>
): asserts props is ConsolePluginProps {
  assertIsDashboardPluginProps(props);
}

export interface FileMetadata {
  id: string | null;
  itemName: string;
}

export function ConsolePlugin(
  props: Partial<ConsolePluginProps>
): JSX.Element | null {
  assertIsConsolePluginProps(props);
  const {
    id,
    hydrateConsole,
    layout,
    panelManager,
    registerComponent,
    notebooksUrl,
  } = props;
  const notebookIndex = useRef(0);
  // Map from file ID to panel ID
  const [openFileMap, setOpenFileMap] = useState(new Map<string, string>());
  const [previewFileMap, setPreviewFileMap] = useState(
    new Map<string, string>()
  );

  useConfigureRuff();

  const dispatch = useDispatch();

  const getConsolePanel = useCallback(
    () => panelManager.getLastUsedPanelOfType(ConsolePanel),
    [panelManager]
  );

  const addOpenFileMapEntry = useCallback(
    (key: string, value: string) => {
      setOpenFileMap(map => new Map(map.set(key, value)));
    },
    [setOpenFileMap]
  );

  const deleteOpenFileMapEntry = useCallback(
    (key: string) => {
      setOpenFileMap(map => {
        map.delete(key);
        return new Map(map);
      });
    },
    [setOpenFileMap]
  );

  const addPreviewFileMapEntry = useCallback(
    (key: string, value: string) => {
      setPreviewFileMap(map => new Map(map.set(key, value)));
    },
    [setPreviewFileMap]
  );

  const deletePreviewFileMapEntry = useCallback(
    (key: string) => {
      setPreviewFileMap(map => {
        map.delete(key);
        return new Map(map);
      });
    },
    [setPreviewFileMap]
  );

  const handleSendCommand = useCallback(
    (command: string, focus = true, execute = true) => {
      const trimmedCommand = command && command.trim();
      if (!trimmedCommand) {
        log.info('Ignoring empty code');
      } else {
        const consolePanel = getConsolePanel();
        if (
          !consolePanel ||
          !(consolePanel instanceof ConsolePanel.WrappedComponent)
        ) {
          log.error('Console panel not found');
          return;
        }
        log.debug('Send command: ', command, focus, execute);
        consolePanel.addCommand(command, focus, execute);
      }
    },
    [getConsolePanel]
  );

  const handleSettingsChanged = useCallback(
    (consoleSettings: Record<string, unknown>) => {
      dispatch(setDashboardConsoleSettings(id, consoleSettings));
    },
    [dispatch, id]
  );

  const getNotebookFileName = useCallback(
    ({ language }: { language?: string }) => {
      const extension = language === 'python' ? 'py' : language;
      let title = null;
      if (extension == null) {
        log.debug('No extension for language', language);
        title = `Untitled-${notebookIndex.current}`;
      } else {
        title = `Untitled-${notebookIndex.current}.${extension}`;
      }
      notebookIndex.current += 1;
      return title;
    },
    []
  );

  const getPanelIdForFileMetadata = useCallback(
    (fileMetadata: FileMetadata, createIfNecessary = true) => {
      const { id: fileId } = fileMetadata;
      if (fileId != null && openFileMap.has(fileId)) {
        return openFileMap.get(fileId);
      }
      if (fileId != null && previewFileMap.has(fileId)) {
        return previewFileMap.get(fileId);
      }
      if (createIfNecessary) {
        return nanoid();
      }
      return undefined;
    },
    [openFileMap, previewFileMap]
  );

  const renamePanel = useCallback(
    (panelId: string, newTitle: string) => {
      LayoutUtils.renameComponent(layout.root, { id: panelId }, newTitle);
    },
    [layout.root]
  );

  const renameFilePanel = useCallback(
    (oldName: string, newName: string) => {
      log.debug('Rename file panel', oldName, newName);
      let panelId;
      if (openFileMap.has(oldName)) {
        panelId = openFileMap.get(oldName);
        deleteOpenFileMapEntry(oldName);
        if (panelId != null) {
          addOpenFileMapEntry(newName, panelId);
        }
      }
      if (previewFileMap.has(oldName)) {
        panelId = previewFileMap.get(oldName);
        deletePreviewFileMapEntry(oldName);
        if (panelId != null) {
          addPreviewFileMapEntry(newName, panelId);
        }
      }

      if (panelId === undefined) {
        log.debug2(`File ${oldName} isn't open, no need to rename the tab`);
        return;
      }

      renamePanel(panelId, FileUtils.getBaseName(newName));
    },
    [
      openFileMap,
      previewFileMap,
      renamePanel,
      addOpenFileMapEntry,
      addPreviewFileMapEntry,
      deleteOpenFileMapEntry,
      deletePreviewFileMapEntry,
    ]
  );

  /**
   * Show the panel for the given file metadata.
   * If the panel is not already open, then it just logs an error and does nothing.
   */
  const showFilePanel = useCallback(
    (fileMetadata: FileMetadata) => {
      const panelId = getPanelIdForFileMetadata(fileMetadata, false);
      if (panelId == null) {
        log.error('Could not find panel id for file metadata', fileMetadata);
        return;
      }
      LayoutUtils.activateTab(layout.root, { id: panelId });
    },
    [getPanelIdForFileMetadata, layout.root]
  );

  const registerFilePanel = useCallback(
    (panelId: string, fileMetadata: FileMetadata, isPreview: boolean) => {
      log.debug('registerFilePanel', panelId, fileMetadata, isPreview);
      if (fileMetadata == null || fileMetadata.id == null) {
        log.debug('Ignore empty file id', fileMetadata);
        return;
      }
      const { id: fileId } = fileMetadata;
      if (isPreview) {
        addPreviewFileMapEntry(fileId, panelId);
        return;
      }
      if (openFileMap.has(fileId)) {
        const existingPanelId = openFileMap.get(fileId);
        if (panelId === existingPanelId) {
          log.debug(`Update tab title for file ${fileId}`);
          const { itemName } = fileMetadata;
          renameFilePanel(fileId, itemName);
        } else {
          log.error(
            `File ${fileId} already associated with a different tab ${existingPanelId}`
          );
        }
        return;
      }

      addOpenFileMapEntry(fileId, panelId);

      // De-register preview tab
      if (previewFileMap.has(fileId)) {
        deletePreviewFileMapEntry(fileId);
      }
    },
    [
      openFileMap,
      previewFileMap,
      renameFilePanel,
      addOpenFileMapEntry,
      addPreviewFileMapEntry,
      deletePreviewFileMapEntry,
    ]
  );

  const unregisterFilePanel = useCallback(
    (fileMetadata: FileMetadata, isPreview: boolean) => {
      log.debug('unregisterFileTab', fileMetadata, isPreview);
      if (fileMetadata == null || fileMetadata.id == null) {
        log.debug('Ignore empty file id', fileMetadata);
        return;
      }
      const { id: fileId } = fileMetadata;
      if (isPreview) {
        deletePreviewFileMapEntry(fileId);
        return;
      }
      deleteOpenFileMapEntry(fileId);
    },
    [deleteOpenFileMapEntry, deletePreviewFileMapEntry]
  );

  const closeFilePanel = useCallback(
    (fileMetadata: FileMetadata, options?: CloseOptions) => {
      log.debug('closeFilePanel', fileMetadata);
      const { id: fileId } = fileMetadata;
      if (fileId == null) {
        log.warn('File ID is null, ignore close event');
        return;
      }
      let panelId = null;
      let isPreview = false;
      if (openFileMap.has(fileId)) {
        panelId = openFileMap.get(fileId);
      } else if (previewFileMap.has(fileId)) {
        panelId = previewFileMap.get(fileId);
        isPreview = true;
      } else {
        log.debug(`File ${fileId} isn't open, ignore close event`);
        return;
      }
      unregisterFilePanel(fileMetadata, isPreview);
      LayoutUtils.closeComponent(layout.root, { id: panelId }, options);
    },
    [layout.root, openFileMap, previewFileMap, unregisterFilePanel]
  );

  const getNotebookTitle = useCallback((fileMetadata: FileMetadata) => {
    const { itemName } = fileMetadata;
    return FileUtils.getBaseName(itemName);
  }, []);

  const fileIsOpen = useCallback(
    (fileMetadata: FileMetadata) => {
      const { id: fileId } = fileMetadata;
      log.debug('fileIsOpen', fileMetadata, fileId, openFileMap);
      return fileId != null && openFileMap.has(fileId);
    },
    [openFileMap]
  );

  const fileIsOpenAsPreview = useCallback(
    (fileMetadata: FileMetadata) => {
      const { id: fileId } = fileMetadata;
      log.debug('fileIsOpenAsPreview', fileMetadata, fileId, previewFileMap);
      return fileId != null && previewFileMap.has(fileId);
    },
    [previewFileMap]
  );

  /*
   * Attempts to focus the panel with the provided panelId
   */
  const focusPanelById = useCallback(
    (panelId: string) => {
      if (panelId == null) {
        return;
      }

      const panel = panelManager.getOpenedPanelById(panelId);
      if (panel && isNotebookPanel(panel)) {
        panel.focus();
      }
    },
    [panelManager]
  );

  const makeConfig = useCallback(
    ({
      id: panelId,
      settings,
      fileMetadata,
      session,
      sessionLanguage,
      isPreview = false,
    }: {
      id: string | undefined;
      settings: CommandHistorySettings;
      fileMetadata: FileMetadata;
      session: dh.IdeSession;
      sessionLanguage: string;
      isPreview?: boolean;
    }) => {
      const panelState = {
        settings,
        fileMetadata,
      };
      const title = getNotebookTitle(fileMetadata);
      return {
        type: 'react-component' as const,
        component: NotebookPanel.COMPONENT,
        isFocusOnShow: false,
        props: {
          localDashboardId: id,
          metadata: { id: panelId },
          session,
          sessionLanguage,
          panelState,
          isPreview,
          notebooksUrl,
        },
        title,
        id: panelId,
      };
    },
    [getNotebookTitle, id, notebooksUrl]
  );

  const createNotebook = useCallback(
    (
      session: dh.IdeSession,
      sessionLanguage: string,
      settings: CommandHistorySettings,
      fileMetadata: FileMetadata = {
        id: null,
        itemName: getNotebookFileName(settings),
      }
    ) => {
      const panelId = getPanelIdForFileMetadata(fileMetadata);
      if (fileIsOpen(fileMetadata) && panelId != null) {
        log.debug('File is already open, focus panel');
        showFilePanel(fileMetadata);
        focusPanelById(panelId);
        return;
      }

      // Should never happen because getPanelForFileMetadata will create an ID by default
      if (panelId == null) {
        log.error('Panel ID for file metadata is null');
        return;
      }
      const stack = LayoutUtils.getStackForComponentTypes(layout.root, [
        NotebookPanel.COMPONENT,
      ]);
      const config = makeConfig({
        id: panelId,
        settings,
        fileMetadata,
        session,
        sessionLanguage,
      });
      log.debug('createNotebook', config, openFileMap);
      LayoutUtils.openComponentInStack(stack, config);
    },
    [
      fileIsOpen,
      focusPanelById,
      getNotebookFileName,
      getPanelIdForFileMetadata,
      layout.root,
      makeConfig,
      openFileMap,
      showFilePanel,
    ]
  );

  const selectNotebook = useCallback(
    (
      session: dh.IdeSession,
      sessionLanguage: string,
      settings: CommandHistorySettings,
      fileMetadata: FileMetadata,
      // linter recognizes shouldFocus as any if I don't specify boolean here
      // eslint-disable-next-line @typescript-eslint/no-inferrable-types
      shouldFocus: boolean = false
    ) => {
      log.debug('selectNotebook', fileMetadata, shouldFocus);
      const isFileOpen = fileIsOpen(fileMetadata);
      const isFileOpenAsPreview = fileIsOpenAsPreview(fileMetadata);

      const [previewId] = [...previewFileMap.values()];
      const isPreview = !shouldFocus;
      const panelId =
        isPreview && previewId
          ? previewId
          : getPanelIdForFileMetadata(fileMetadata);

      if (panelId == null || panelId === '') {
        throw new Error(
          `Unable to retrieve or create panelId for metadata: ${JSON.stringify(
            fileMetadata
          )}`
        );
      }

      // If the file is already open, show it
      if (isFileOpen || isFileOpenAsPreview) {
        showFilePanel(fileMetadata);
      } else {
        const stack = LayoutUtils.getStackForComponentTypes(layout.root, [
          NotebookPanel.COMPONENT,
        ]);

        const config = makeConfig({
          id: panelId,
          settings,
          fileMetadata,
          session,
          sessionLanguage,
          isPreview,
        });

        // This will replace the existing preview by panelId if needed
        LayoutUtils.openComponentInStack(stack, config);
      }

      // If the file is open as a preview and focused, promote to non-preview
      if (isFileOpenAsPreview && shouldFocus) {
        const fileId = fileMetadata.id;
        const stack = LayoutUtils.getStackForConfig(layout.root, {
          component: NotebookPanel.COMPONENT,
          id: panelId,
        });

        const item = LayoutUtils.getContentItemInStack(stack, {
          component: NotebookPanel.COMPONENT,
          id: panelId,
        });
        if (fileId != null && item && isComponent(item)) {
          item.container.emit(NotebookEvent.PROMOTE_FROM_PREVIEW);
          deletePreviewFileMapEntry(fileId);
          addOpenFileMapEntry(fileId, panelId);
        }
      }

      if (shouldFocus) {
        // Focus the tab if we're supposed to
        focusPanelById(panelId);
      }
    },
    [
      showFilePanel,
      fileIsOpen,
      fileIsOpenAsPreview,
      focusPanelById,
      getPanelIdForFileMetadata,
      layout.root,
      makeConfig,
      previewFileMap,
      deletePreviewFileMapEntry,
      addOpenFileMapEntry,
    ]
  );

  /** Attempts to send the text to a notebook matching the passed in settings */
  const sendToNotebook = useCallback(
    (
      session: dh.IdeSession,
      sessionLanguage: string,
      settings: CommandHistorySettings = {} as CommandHistorySettings,
      createIfNecessary = true
    ) => {
      const notebookPanel = panelManager.getLastUsedPanelOfType(NotebookPanel);
      if (notebookPanel && isNotebookPanel(notebookPanel)) {
        if (
          settings != null &&
          settings.value != null &&
          notebookPanel.notebook != null
        ) {
          notebookPanel.notebook.append(settings.value);
        }
      } else if (createIfNecessary) {
        createNotebook(session, sessionLanguage, settings);
      }
    },
    [createNotebook, panelManager]
  );

  const hydrateNotebook = useCallback(
    (
      panelProps: DehydratedPanelProps,
      panelDashboardId: string
    ): DehydratedDashboardPanelProps =>
      DashboardUtils.hydrate(
        {
          ...panelProps,
          notebooksUrl,
        },
        panelDashboardId
      ),
    [notebooksUrl]
  );

  usePanelRegistration(registerComponent, ConsolePanel, hydrateConsole);
  usePanelRegistration(registerComponent, CommandHistoryPanel);
  usePanelRegistration(registerComponent, FileExplorerPanel);
  usePanelRegistration(registerComponent, LogPanel);
  usePanelRegistration(registerComponent, NotebookPanel, hydrateNotebook);

  useListener(layout.eventHub, ConsoleEvent.SEND_COMMAND, handleSendCommand);
  useListener(
    layout.eventHub,
    ConsoleEvent.SETTINGS_CHANGED,
    handleSettingsChanged
  );
  useListener(layout.eventHub, NotebookEvent.CLOSE_FILE, closeFilePanel);
  useListener(layout.eventHub, NotebookEvent.CREATE_NOTEBOOK, createNotebook);
  useListener(layout.eventHub, NotebookEvent.SELECT_NOTEBOOK, selectNotebook);
  useListener(layout.eventHub, NotebookEvent.RENAME, renamePanel);
  useListener(layout.eventHub, NotebookEvent.RENAME_FILE, renameFilePanel);
  useListener(layout.eventHub, NotebookEvent.SEND_TO_NOTEBOOK, sendToNotebook);
  useListener(layout.eventHub, NotebookEvent.REGISTER_FILE, registerFilePanel);
  useListener(
    layout.eventHub,
    NotebookEvent.UNREGISTER_FILE,
    unregisterFilePanel
  );

  return null;
}

export default ConsolePlugin;
